/**
 * Wörter und kleine Rechnungen für die Seite „Nutzung" (HM-168).
 *
 * Gerechnet ist der Bericht auf dem Server (`buildUsageReport` in der Domäne);
 * hier wird nur benannt, gezählt und für das Diagramm umgeformt.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import type { UsagePlatformFilter } from "./router.js";

type Report = ApiComponents["schemas"]["AdminUsageReport"];
type Entry = ApiComponents["schemas"]["UsageEntry"];

export const PLATFORM_LABELS: Record<UsagePlatformFilter, string> = {
  web: "Web-App",
  android: "Android",
  ios: "iOS",
};

const COUNT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const AVERAGE = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });

export function formatCount(value: number): string {
  return COUNT.format(value);
}

/** Ein Mittel von Menschen je Tag — eine Nachkommastelle, sonst wäre 0,4 „0". */
export function formatAverage(value: number | null): string | null {
  return value === null ? null : AVERAGE.format(value);
}

/**
 * „Web-App 12 · Android 3" — nur Plattformen mit Aufrufen. Eine Null je
 * Plattform in jeder Zeile wäre an einer Installation ohne iOS-App ein Drittel
 * Rauschen.
 */
export function platformSplit(byPlatform: Entry["byPlatform"]): string {
  return (Object.keys(PLATFORM_LABELS) as UsagePlatformFilter[])
    .filter((platform) => byPlatform[platform] > 0)
    .map((platform) => `${PLATFORM_LABELS[platform]} ${formatCount(byPlatform[platform])}`)
    .join(" · ");
}

/**
 * Was in der Zeile steht: die Bezeichnung aus Katalog oder Vertrag, sonst der
 * Schlüssel selbst. Ein Schlüssel ohne Bezeichnung ist ein Endpunkt, den es
 * nicht mehr gibt, oder ein Eintrag aus einer neueren Fassung — beides soll man
 * sehen und nicht hinter einem „Unbekannt" verstecken (Regel 9).
 */
export function entryTitle(entry: Entry): string {
  return entry.label ?? entry.name;
}

/** Die Punkte des Diagramms — `null` vor Beginn der Zählung, keine Null (Regel 8). */
export function activeUserPoints(report: Pick<Report, "days">): { at: number; value: number | null }[] {
  return report.days.map((day) => ({ at: new Date(day.startsAt).getTime(), value: day.activeUsers }));
}

/* ---- HM-174: Wochen, Zeiten, Vergleich ------------------------------------ */

type Heatmap = Report["heatmap"];
type HeatmapCell = Heatmap["cells"][number];

/** Montag zuerst, wie die Heatmap und ISO 8601. */
export const WEEKDAY_SHORT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
export const WEEKDAY_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"] as const;

export function weekdayShort(weekday: number): string {
  return WEEKDAY_SHORT[weekday - 1] ?? String(weekday);
}

/** „Di 20–21 Uhr" — eine Stunde nennt beide Kanten, sonst liest sie sich als Zeitpunkt. */
export function hourSlotLabel(weekday: number, hour: number): string {
  return `${weekdayShort(weekday)} ${hour}–${hour + 1} Uhr`;
}

/**
 * Die Wochenpunkte des Verlaufs: das Tagesmittel jeder Woche. Eine Teilwoche
 * trägt ihren Anteil gezählter Tage als `coverage` und wird blasser gezeichnet
 * — ihr Mittel gilt nur für diese Tage; ohne gezählten Tag ist sie eine Lücke.
 */
export function weekPoints(report: Pick<Report, "weeks">): { at: number; value: number | null; coverage?: number }[] {
  return report.weeks.map((week) => ({
    at: new Date(week.startsAt).getTime(),
    value: week.averageActiveUsers,
    ...(week.days > 0 && week.days < 7 ? { coverage: week.days / 7 } : {}),
  }));
}

/**
 * Die Veränderung gegenüber dem Vorzeitraum, als Anteil (`0.12` = +12 %).
 * `null`, wenn es keinen ganz gezählten Vorzeitraum gibt oder er null war —
 * aus null wird kein Prozent, sondern eine neue Zahl.
 */
export function changeAgainst(current: number | null, previous: number | null | undefined): number | null {
  if (current === null || previous === null || previous === undefined || previous === 0) return null;
  return current / previous - 1;
}

const PERCENT = new Intl.NumberFormat("de-DE", { style: "percent", maximumFractionDigits: 0, signDisplay: "exceptZero" });
const SHARE = new Intl.NumberFormat("de-DE", { style: "percent", maximumFractionDigits: 0 });

/** „+12 %", „−5 %", „±0 %" — mit Vorzeichen, weil es eine Veränderung ist. */
export function formatChange(change: number): string {
  // Das echte Minuszeichen statt des Bindestrichs, den `Intl` setzt.
  return Math.abs(change) < 0.005 ? "±0\u00a0%" : PERCENT.format(change).replace("-", "\u2212");
}

/** Ein Anteil 0…1 als „64 %"; unter einem halben Prozent „< 1 %" statt einer Null, die keine ist. */
export function formatShare(share: number | null): string | null {
  if (share === null) return null;
  if (share > 0 && share < 0.005) return "< 1\u00a0%";
  return SHARE.format(share);
}

/**
 * Die Stufe einer Zelle der Heatmap: `null` unbekannt, `0` gezählt und niemand
 * da, `1`…`4` in Vierteln des stärksten Felds. Feste Stufen statt einer
 * stufenlosen Fläche: Vier Töne kann man in der Legende wiederfinden, einen
 * stufenlosen nicht.
 */
export type HeatLevel = null | 0 | 1 | 2 | 3 | 4;

export function heatLevel(value: number | null, max: number): HeatLevel {
  if (value === null) return null;
  if (value <= 0 || max <= 0) return 0;
  return Math.min(4, Math.max(1, Math.ceil((value / max) * 4))) as HeatLevel;
}

export function heatmapMax(heatmap: Pick<Heatmap, "cells">): number {
  return heatmap.cells.reduce((max, cell) => Math.max(max, cell.averageActiveUsers ?? 0), 0);
}

/** Die stärksten Felder — für eine Zeile Text unter der Heatmap und für die Sprachausgabe. */
export function busiestSlots(heatmap: Pick<Heatmap, "cells">, count = 3): HeatmapCell[] {
  return heatmap.cells
    .filter((cell) => (cell.averageActiveUsers ?? 0) > 0)
    .sort((a, b) => (b.averageActiveUsers ?? 0) - (a.averageActiveUsers ?? 0) || a.weekday - b.weekday || a.hour - b.hour)
    .slice(0, count);
}

/** Der Wochentag mit dem höchsten Tagesmittel; `null` ohne gezählten Tag mit Nutzung. */
export function busiestWeekday(weekdays: Report["weekdays"]): Report["weekdays"][number] | null {
  return weekdays.reduce<Report["weekdays"][number] | null>(
    (best, day) =>
      (day.averageActiveUsers ?? 0) > 0 && (best === null || (day.averageActiveUsers ?? 0) > (best.averageActiveUsers ?? 0))
        ? day
        : best,
    null,
  );
}
